import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dashApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, Loader2, Lock, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AuthDisclaimer } from "@/components/AuthDisclaimer";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Lien invalide");
    if (form.new_password.length < 6) return toast.error("Le mot de passe doit faire 6 caractères minimum");
    if (form.new_password !== form.confirm) return toast.error("La confirmation ne correspond pas");
    setLoading(true);
    try {
      await dashApi.resetPassword(token, form.new_password);
      setDone(true);
      toast.success("Mot de passe réinitialisé");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la réinitialisation");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0B1120]">
      <div className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(234,179,8,0.12), transparent 40%)" }} />

      <div className="max-w-md w-full bg-slate-900/90 border border-slate-800 rounded-2xl shadow-2xl p-8 backdrop-blur-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 flex">
          <div className="flex-1 bg-emerald-600" />
          <div className="flex-1 bg-yellow-500" />
          <div className="flex-1 bg-red-600" />
        </div>

        <div className="flex items-center justify-center gap-3 mb-6 pb-5 border-b border-slate-800">
          <div className="w-12 h-12 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="text-left">
            <h1 className="font-display text-xl font-bold text-slate-100 leading-tight">Sama Jaamu</h1>
            <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Nouveau mot de passe</p>
          </div>
        </div>

        {done ? (
          <div className="text-center py-4" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-slate-100 font-display font-semibold">Mot de passe réinitialisé</p>
            <p className="text-sm text-slate-400 mt-2">Redirection vers la page de connexion...</p>
          </div>
        ) : !token ? (
          <div className="text-center py-4">
            <p className="text-red-400 font-display font-semibold">Lien invalide</p>
            <p className="text-sm text-slate-400 mt-2">Ce lien de réinitialisation est incomplet ou expiré.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="reset-new-password" type="password" value={form.new_password} onChange={set("new_password")} className="pl-10 bg-slate-950 border-slate-700 text-slate-100 h-11" placeholder="••••••••••" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="reset-confirm-password" type="password" value={form.confirm} onChange={set("confirm")} className="pl-10 bg-slate-950 border-slate-700 text-slate-100 h-11" placeholder="••••••••••" required />
              </div>
            </div>
            <Button type="submit" data-testid="reset-submit-btn" disabled={loading} className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réinitialiser le mot de passe"}
            </Button>
          </form>
        )}

        <AuthDisclaimer />

        <div className="mt-6 text-center">
          <button type="button" onClick={() => navigate("/login")} className="text-xs font-mono text-slate-400 hover:text-slate-200 inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
